import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiClock, FiUsers, FiTrendingUp } = FiIcons;

const CampaignGoal = () => {
  const [currentCount, setCurrentCount] = useState(347);
  const target = 500;
  const timeRemaining = "5 months 12 days";
  const progress = (currentCount / target) * 100;

  // Live donor ticker
  const recentDonors = [
    { name: "Anonymous", amount: "₹2,000", time: "2 min ago" },
    { name: "Priya Sharma", amount: "₹5,000", time: "8 min ago" },
    { name: "Raj Patel", amount: "₹1,000", time: "15 min ago" },
    { name: "Anonymous", amount: "₹10,000", time: "23 min ago" },
    { name: "Meera Singh", amount: "₹3,500", time: "31 min ago" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.8) {
        setCurrentCount(prev => Math.min(prev + 1, target));
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [target]);

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiTarget} className="w-4 h-4" />
            <span>Campaign Milestone</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Ambitious
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
              Goal
            </span>
          </h2>
          
          <p className="text-2xl font-semibold text-gray-700 max-w-4xl mx-auto leading-relaxed">
            We aim to <strong className="text-orange-600">rescue and rehabilitate 500 individuals</strong> in the next 6 months with your support
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Progress and Stats */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Main Progress */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-orange-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Lives Transformed</h3>
                <div className="flex items-center space-x-2 text-orange-600">
                  <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
                  <span className="font-semibold">Live Progress</span>
                </div>
              </div>

              {/* Progress Numbers */}
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <motion.div 
                    className="text-4xl font-bold text-green-600 mb-2"
                    initial={{ scale: 0.5 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    {currentCount}
                  </motion.div>
                  <div className="text-gray-600 font-medium">Rescued & Rehabilitated</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900 mb-2">{target}</div>
                  <div className="text-gray-600 font-medium">Target Goal</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Progress</span>
                  <span>{progress.toFixed(1)}% Complete</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-green-500 via-orange-500 to-red-500 rounded-full relative"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${progress}%` }}
                    transition={{ duration: 2, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white text-xs font-bold">
                      {progress.toFixed(0)}%
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Time Remaining */}
              <div className="flex items-center justify-center space-x-2 text-orange-600 bg-orange-50 rounded-lg p-4">
                <SafeIcon icon={FiClock} className="w-5 h-5" />
                <span className="font-semibold">{timeRemaining} remaining</span>
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center shadow-lg"
              >
                <div className="text-3xl font-bold text-blue-600 mb-2">₹25L</div>
                <div className="text-gray-600 font-medium">Funds Needed</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center shadow-lg"
              >
                <div className="text-3xl font-bold text-green-600 mb-2">1,247</div>
                <div className="text-gray-600 font-medium">Supporters</div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right - Live Donor Activity */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Live Donor Ticker */}
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <h3 className="text-xl font-bold text-gray-900">Live Donations</h3>
              </div>
              
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {recentDonors.map((donor, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {donor.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{donor.name}</div>
                        <div className="text-sm text-gray-500">{donor.time}</div>
                      </div>
                    </div>
                    <div className="font-bold text-green-600">{donor.amount}</div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Be Part of This Milestone</h3>
              <p className="text-orange-100 mb-6 text-lg">
                Join {currentCount} others who have already made a difference. Help us reach our goal of transforming 500 lives.
              </p>
              
              <div className="space-y-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-white text-orange-600 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  🎯 Help Us Reach Our Goal - Donate Now
                </motion.button>
                
                <div className="text-sm text-orange-200">
                  ✅ 80G Tax Benefits  •  ✅ Instant Receipt  •  ✅ Monthly Updates
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CampaignGoal;